import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

const getWorkspaceId = (workspace) => workspace?.id || workspace?._id || null

const isSameId = (a, b) => String(a || '') === String(b || '')

const findWorkspaceById = (workspaces, workspaceId) => {
    if (!workspaceId || !Array.isArray(workspaces)) return null
    return workspaces.find(workspace => isSameId(getWorkspaceId(workspace), workspaceId)) || null
}

/**
 * Selection store for workspace/project context
 * This manages the current workspace and project selection
 */
export const useSelectionStore = create(
    persist(
        (set, get) => ({
            // Current selections
            activeWorkspaceId: null,
            activeProjectId: null,

            // Workspace data (cached for quick access)
            workspaces: [],
            currentWorkspace: null,

            // Project data (cached for quick access) 
            projects: [],
            currentProject: null,

            // Active chatbot per workspace — shared across the Inbox/Manage pages
            activeBotIdByWorkspace: {},
            setActiveBotId: (workspaceId, botId) => {
                if (!workspaceId) return
                const current = get().activeBotIdByWorkspace || {}
                if (current[workspaceId] === (botId || null)) return
                set({ activeBotIdByWorkspace: { ...current, [workspaceId]: botId || null } })
            },

            // AI Companion Mode (project-specific feature flag)
            aiCompanionMode: false,
            setAiCompanionMode: (mode) => {
                const newMode = mode || false
                if (newMode === get().aiCompanionMode) return
                set({ aiCompanionMode: newMode })
            },

            // Actions
            setActiveWorkspace: (workspaceId) => {
                const currentWs = get().activeWorkspaceId
                console.log('[selection.store] setActiveWorkspace:', workspaceId, 'current:', currentWs, 'currentProject before:', get().currentProject?.id, get().currentProject?.uniqueUrl)
                const nextWorkspaceId = workspaceId || null
                const nextWorkspace = findWorkspaceById(get().workspaces, nextWorkspaceId)
                // Only clear project data when workspace actually changes
                // This preserves shared project data when navigating within the same workspace context
                if (currentWs && isSameId(currentWs, nextWorkspaceId)) {
                    const currentWorkspaceId = getWorkspaceId(get().currentWorkspace)
                    if (nextWorkspace && !isSameId(currentWorkspaceId, nextWorkspaceId)) {
                        set({ currentWorkspace: nextWorkspace })
                    }
                    console.log('[selection.store] setActiveWorkspace: same workspace, skipping clear')
                    return
                }
                set({
                    activeWorkspaceId: nextWorkspaceId,
                    currentWorkspace: nextWorkspace,
                    activeProjectId: null, // Clear project when switching workspace
                    currentProject: null,
                    aiCompanionMode: false, // Clear AI mode when switching workspace
                })
                console.log('[selection.store] setActiveWorkspace: cleared project data')
            },

            setActiveProject: (projectId) => {
                // Skip if already set to the same project (prevents infinite re-render loops)
                if (projectId === get().activeProjectId) return
                const existing = get().currentProject
                const fromList = get().projects.find(p => p.id === projectId)
                const resolved = fromList || ((existing?.id || existing?._id) === projectId ? existing : null)
                console.log('[selection.store] setActiveProject:', projectId, 'existing:', existing?.id, existing?.uniqueUrl, 'fromList:', !!fromList, 'resolved:', resolved?.id, resolved?.uniqueUrl)
                set({
                    activeProjectId: projectId,
                    currentProject: resolved,
                    aiCompanionMode: false,
                })
            },

            // Update cached workspace data
            setWorkspaces: (workspaces) => {
                const activeId = get().activeWorkspaceId
                const current = findWorkspaceById(workspaces, activeId)
                set({
                    workspaces,
                    currentWorkspace: current,
                })
            },

            setCurrentWorkspace: (workspace) => {
                set({ currentWorkspace: workspace })
            },

            // Update cached project data
            setProjects: (projects) => {
                const activeId = get().activeProjectId
                const current = projects.find(p => p.id === activeId) || null
                set({
                    projects,
                    currentProject: current,
                })
            },

            setCurrentProject: (project) => {
                set({
                    currentProject: project,
                    activeProjectId: project?.id || null,
                })
            },

            // Clear all selections (logout, etc.)
            clearSelections: () => {
                set({
                    activeWorkspaceId: null,
                    activeProjectId: null,
                    workspaces: [],
                    projects: [],
                    currentWorkspace: null,
                    currentProject: null,
                    aiCompanionMode: false,
                })
            },

            // Computed getters
            getEffectiveWorkspaceId: () => {
                return get().activeWorkspaceId
            },

            // Add workspace/project getters for convenience
            get workspace() {
                return get().activeWorkspaceId
            },

            get project() {
                return get().activeProjectId
            },

            hasWorkspaceSelected: () => {
                return get().activeWorkspaceId !== null
            },

            hasProjectSelected: () => {
                return get().activeProjectId !== null
            },

            // Get workspace/project info for URL building
            getSelectionParams: () => {
                const { activeWorkspaceId, activeProjectId } = get()
                return {
                    workspace: activeWorkspaceId || null,
                    project: activeProjectId,
                }
            },

            // Navigation helpers
            switchWorkspace: (workspaceId) => {
                const nextWorkspaceId = workspaceId || null
                const nextWorkspace = findWorkspaceById(get().workspaces, nextWorkspaceId)
                set({
                    activeWorkspaceId: nextWorkspaceId,
                    currentWorkspace: nextWorkspace,
                    activeProjectId: null,
                    currentProject: null,
                    aiCompanionMode: false,
                })
                // Update URL
                const searchParams = new URLSearchParams(window.location.search)
                if (nextWorkspaceId) {
                    searchParams.set('workspace', nextWorkspaceId)
                } else {
                    searchParams.delete('workspace')
                }
                searchParams.delete('project')
                window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`)
            },

            switchProject: (projectId) => {
                set({
                    activeProjectId: projectId,
                    currentProject: get().projects.find(p => p.id === projectId) || null,
                    aiCompanionMode: false, // Clear AI mode when switching project
                })
                // Update URL
                const searchParams = new URLSearchParams(window.location.search)
                if (projectId) {
                    searchParams.set('project', projectId)
                } else {
                    searchParams.delete('project')
                }
                window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`)
            },
        }),
        {
            name: 'codedesign-selection',
            storage: createJSONStorage(() => localStorage),
            // Only persist the IDs, not the full data objects
            partialize: (state) => ({
                activeWorkspaceId: state.activeWorkspaceId,
                activeProjectId: state.activeProjectId,
                currentProject: state.currentProject,
                activeBotIdByWorkspace: state.activeBotIdByWorkspace,
            }),
        }
    )
)

// Convenient hooks for common use cases
export const useActiveWorkspace = () => {
    const store = useSelectionStore()
    return {
        workspaceId: store.getEffectiveWorkspaceId(),
        workspace: store.currentWorkspace,
        hasWorkspace: store.hasWorkspaceSelected(),
        setWorkspace: store.setActiveWorkspace,
    }
}

export const useActiveProject = () => {
    const store = useSelectionStore()
    return {
        projectId: store.activeProjectId,
        project: store.currentProject,
        hasProject: store.hasProjectSelected(),
        setProject: store.setActiveProject,
    }
}

export const useWorkspaceProjects = () => {
    const store = useSelectionStore()
    return {
        projects: store.projects,
        setProjects: store.setProjects,
    }
}

// Hook for navigation guards
export const useSelectionParams = () => {
    return useSelectionStore(state => state.getSelectionParams())
}

// Hook for AI Companion Mode (direct store access)
export const useAiCompanionModeStore = () => {
    const isAiCompanionMode = useSelectionStore(state => state.aiCompanionMode)
    const setAiCompanionMode = useSelectionStore(state => state.setAiCompanionMode)
    return {
        isAiCompanionMode,
        setAiCompanionMode,
    }
}
